import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom'; // Import context hook
import { useNavigate } from 'react-router-dom';
import { 
  DocumentTextIcon, 
  CurrencyDollarIcon, 
  ClockIcon, 
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ChevronRightIcon,
  BellIcon,
  MenuIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/outline';
import useAuthStore from '../../store/authStore';
import { getContractsForUser, getActivitiesForUser, Contract, Activity } from '../../services/contractService';

// بيانات وهمية للعرض
const summaryItems = (userRole: 'client' | 'lawyer' = 'client') => {
  const allItems = [
    {
      name: 'العقود النشطة',
      value: '٣',
      icon: DocumentTextIcon,
      color: 'bg-blue-100 text-blue-600',
      roles: ['client', 'lawyer'],
    },
    {
      name: 'إجمالي المدفوعات',
      value: '١٢,٠٠٠ ر.س',
      icon: CurrencyDollarIcon,
      color: 'bg-green-100 text-green-600',
      roles: ['client'],
    },
    {
      name: 'الدفعة القادمة',
      value: '٢,٠٠٠ ر.س',
      icon: ClockIcon,
      color: 'bg-amber-100 text-amber-600',
      dueDate: 'بعد ٣ أيام',
      roles: ['client'],
    },
    {
      name: 'عقود مكتملة',
      value: '٥',
      icon: CheckCircleIcon,
      color: 'bg-purple-100 text-purple-600',
      roles: ['client', 'lawyer'],
    },
  ];

  return allItems.filter(item => item.roles.includes(userRole));
};

const getActivityIcon = (type: Activity['type'], userRole: 'client' | 'lawyer' = 'client') => {
  switch (type) {
    case 'تم الدفع':
      return { Icon: CurrencyDollarIcon, color: 'bg-green-100 text-green-600' };
    case 'تم توقيع العقد':
      return { Icon: DocumentTextIcon, color: 'bg-blue-100 text-blue-600' };
    case 'تم رفع المستند':
      return { Icon: DocumentDuplicateIcon, color: 'bg-purple-100 text-purple-600' };
    case 'موافقة على عقد':
        return { Icon: CheckCircleIcon, color: 'bg-green-100 text-green-600' };
    default:
      return { Icon: CheckCircleIcon, color: 'bg-gray-50 text-gray-600' };
  }
};

// دالة لحساب الوقت المنقضي
const timeAgo = (date: Date) => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) return `منذ ${Math.floor(interval)} سنوات`;
  interval = seconds / 2592000;
  if (interval > 1) return `منذ ${Math.floor(interval)} أشهر`;
  interval = seconds / 86400;
  if (interval > 1) return `منذ ${Math.floor(interval)} أيام`;
  interval = seconds / 3600;
  if (interval > 1) return `منذ ${Math.floor(interval)} ساعات`;
  interval = seconds / 60;
  if (interval > 1) return `منذ ${Math.floor(interval)} دقائق`;
  return `منذ ${Math.floor(seconds)} ثوان`;
};

const recentActivities = (userRole: 'client' | 'lawyer' = 'client') => {
  const clientActivities = [
    {
      id: 1,
      type: 'تم الدفع',
      description: '١,٥٠٠ ر.س إلى تيك كورب',
      time: 'منذ ساعتين',
      icon: CurrencyDollarIcon,
      color: 'bg-green-100 text-green-600',
    },
    {
      id: 2,
      type: 'تم توقيع العقد',
      description: 'عقد استشاري - المرحلة الثانية',
      time: 'منذ يوم',
      icon: DocumentTextIcon,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      id: 3,
      type: 'تم رفع المستند',
      description: 'فاتورة رقم #INV-00123.pdf',
      time: 'منذ ٣ أيام',
      icon: DocumentDuplicateIcon,
      color: 'bg-purple-100 text-purple-600',
    },
  ];

  const lawyerActivities = [
    {
      id: 1,
      type: 'موافقة على عقد',
      description: 'تمت الموافقة على عقد أتعاب مع العميل فهد الغامدي',
      time: 'منذ ٥ دقائق',
      icon: CheckCircleIcon,
      color: 'bg-green-100 text-green-600',
    },
    {
      id: 2,
      type: 'تم رفع المستند',
      description: 'تم رفع مذكرة الرد في قضية شركة ABC',
      time: 'منذ ساعة',
      icon: DocumentDuplicateIcon,
      color: 'bg-purple-100 text-purple-600',
    },
  ];

  return userRole === 'lawyer' ? lawyerActivities : clientActivities;
};

type LayoutContext = { openMenu: () => void }; // Sidebar open function from layout

const ClientDashboard: React.FC = () => {
  const { openMenu } = useOutletContext<LayoutContext>(); // Get openMenu from layout
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoadingContracts, setIsLoadingContracts] = useState(true);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [isHamburger, setIsHamburger] = useState(true);
  const menuInterval = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsHamburger(prev => !prev);
    }, 3000);
    menuInterval.current = interval;

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!userData?.uid || !userData.role) return;

      console.log('ClientDashboard: Fetching data for user:', JSON.stringify(userData, null, 2));

      // جلب العقود
      setIsLoadingContracts(true);
      getContractsForUser(userData.uid, userData.role)
        .then(userContracts => {
          console.log(`ClientDashboard: Found ${userContracts.length} total contracts for user.`);
          
          // Remove duplicate contracts by ID
          const uniqueContracts = userContracts.filter(
            (contract, index, self) => 
              index === self.findIndex(
                (c) => c.id === contract.id
              )
          );
          
          console.log(`ClientDashboard: After deduplication, found ${uniqueContracts.length} unique contracts.`);
          
          // Filter for active/pending contracts
          const activeContracts = uniqueContracts.filter(c => c.status === 'نشط' || c.status === 'قيد الانتظار');
          console.log(`ClientDashboard: Found ${activeContracts.length} active/pending contracts.`);
          
          setContracts(activeContracts);
        })
        .catch(error => {
          console.error('Error fetching contracts:', error);
        })
        .finally(() => {
          setIsLoadingContracts(false);
        });

      // جلب الأنشطة
      setIsLoadingActivities(true);
      getActivitiesForUser(userData.uid)
        .then(userActivities => {
          console.log(`ClientDashboard: Found ${userActivities.length} activities.`);
          console.log('ClientDashboard: Fetched activities:', userActivities);
          setActivities(userActivities);
        })
        .catch(error => {
          console.error('Error fetching activities:', error);
        })
        .finally(() => {
          setIsLoadingActivities(false);
        });
    };

    fetchData();
  }, [userData]);

  return (
    <div className="p-5 bg-gray-50 space-y-6 font-sans pb-32 text-right dir-rtl">
      {/* قسم الترحيب مع الأزرار */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2 space-x-reverse">
          <div className="flex items-center">
            <button 
              onClick={() => openMenu()}
              style={{
                width: '2.5rem',
                height: '2.5rem',
                borderRadius: '9999px',
                backgroundColor: '#000000',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.3s',
                marginLeft: '0.75rem',
                border: 'none',
                cursor: 'pointer',
                outline: 'none'
              }}
              onMouseOver={e => e.currentTarget.style.backgroundColor = 'oklch(0.5 0.24 262.91)'}
              onMouseOut={e => e.currentTarget.style.backgroundColor = 'oklch(0.55 0.24 262.91)'}
              onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px rgba(99, 102, 241, 0.5)'}
              onBlur={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{
                position: 'relative',
                width: '1.25rem',
                height: '1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <MenuIcon 
                  style={{
                    width: '1.25rem',
                    height: '1.25rem',
                    color: '#ffffff',
                    position: 'absolute',
                    transition: 'all 0.3s',
                    opacity: isHamburger ? 1 : 0
                  }} 
                />
                <span 
                  style={{
                    position: 'absolute',
                    fontWeight: 700,
                    color: '#ffffff',
                    transition: 'all 0.3s',
                    opacity: isHamburger ? 0 : 1,
                    transform: isHamburger ? 'scale(0)' : 'scale(1)'
                  }}
                >
                  L
                </span>
              </div>
            </button>
            <h2 className="text-2xl font-bold text-gray-900">{userData?.fullName || 'مستخدم'}</h2>
          </div>
        </div>
        
        <button 
          className="relative p-2 rounded-full hover:bg-gray-200 transition-colors"
          onClick={() => navigate('/notifications')}
        >
          <BellIcon className="h-5 w-5 text-gray-700" />
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
            3
          </span>
        </button>
      </div>

      {/* بطاقات ملخصة */}
      <div className="grid grid-cols-2 gap-3">
        {summaryItems(userData?.role).map((item, index) => (
          <div key={index} className={`p-4 rounded-lg bg-white border border-gray-50`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{item.name}</p>
                <p className="text-xl font-bold">{item.value}</p>
                {item.dueDate && (
                  <p className="text-xs mt-1">{item.dueDate}</p>
                )}
              </div>
              <div className={`p-2 rounded-full ${item.color} bg-opacity-30`}>
                <item.icon className="h-5 w-5" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* الدفعة القادمة أو تسجيل عقد جديد */}
      {userData?.role === 'client' ? (
        <div className="bg-gradient-to-r from-blue-600 to-blue-500 rounded-2xl p-5 text-white">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium text-blue-100">الدفعة القادمة</p>
              <p className="text-2xl font-bold mt-1">٢,٠٠٠ ر.س</p>
              <p className="text-xs text-blue-100 mt-1">بعد ٣ أيام • اتفاقية خدمة</p>
            </div>
            <button className="bg-white text-blue-600 text-sm font-semibold px-4 py-2 rounded-lg shadow-md">
              الدفع الآن
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-r from-green-600 to-green-500 rounded-2xl p-5 text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-lg font-bold">مستعد لعميل جديد؟</p>
              <p className="text-sm text-green-100 mt-1">ابدأ بتسجيل عقد جديد بكل سهولة</p>
            </div>
            <button 
              onClick={() => navigate('/contracts/create')}
              className="bg-white text-green-600 text-sm font-semibold px-4 py-2 rounded-lg shadow-md">
              تسجيل عقد
            </button>
          </div>
        </div>
      )}

      {/* العقود النشطة */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">العقود النشطة</h2>
          <button 
            onClick={() => navigate('/contracts')} 
            className="text-blue-600 text-sm"
          >
            عرض الكل
          </button>
        </div>
        <div className="space-y-3">
          {isLoadingContracts ? (
            <p className="text-center text-gray-500">جاري تحميل العقود...</p>
          ) : contracts.length > 0 ? (
            contracts.slice(0, 3).map((contract) => (
              <div 
                key={contract.id} 
                onClick={() => navigate(`/contracts/${contract.id}`)}
                className="bg-white rounded-2xl p-4 border border-gray-50 active:bg-gray-50 transition-colors cursor-pointer"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-semibold text-gray-900">{contract.title}</p>
                    {userData?.role !== 'lawyer' && (
                      <p className="text-xs text-gray-500 mt-1">
                        الموعد النهائي: {new Date(contract.dueDate).toLocaleDateString('en-SA')}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    {userData?.role !== 'lawyer' && (
                      <p className="font-bold text-gray-900">
                        {contract.amount.toLocaleString('en-SA')} {contract.currency}
                      </p>
                    )}
                    <span 
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 ${
                        contract.status === 'نشط' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-amber-100 text-amber-800' 
                      }`}>
                      {contract.status}
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500">لا توجد عقود نشطة حالياً.</p>
          )}
        </div>
      </div>

      {/* النشاطات الأخيرة */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">النشاطات الأخيرة</h2>
          <button className="text-blue-600 text-sm">عرض الكل</button>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-50">
          <div className="space-y-4">
            {isLoadingActivities ? (
              <p className="text-center text-gray-500">جاري تحميل الأنشطة...</p>
            ) : activities.length > 0 ? (
              activities.slice(0, 3).map((activity) => { 
                const { Icon, color } = getActivityIcon(activity.type, userData?.role);
                return (
                  <div key={activity.id} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${color} bg-opacity-30`}>
                        <Icon className="h-5 w-5" />
                      </div>
                    </div>
                    <div className="mr-3 flex-1">
                      <div className="flex justify-between">
                        <p className="text-sm font-medium text-gray-900">{activity.type}</p>
                        <span className="text-xs text-gray-400">{timeAgo(activity.timestamp)}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{activity.description}</p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-gray-500">لا توجد أنشطة لعرضها.</p>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default ClientDashboard;
